import { EllipsisVerticalIcon, SparklesIcon } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  ActionMenu,
  menuTriggerClassName,
} from '@/components/organisms/Dialogs/ActionMenu/ActionMenu';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import { useCopyPaste } from '@/hooks/useCopyPaste';
import useMenu from '@/hooks/useMenu';
import { iconByType, languageSupportsEnhance } from '@/redux/dialogs/helper';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { isModalOpen } from '@/redux/modals/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Node } from '@/shared/types/node';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import styles from './NodeHeader.module.scss';

type Props = {
  type: string;
  nodeId: string;
  isRootNodeUnknown?: boolean;
  node: Node;
  isSelected: boolean;
};

const NodeHeader = ({ type, nodeId, isRootNodeUnknown = true, node, isSelected }: Props) => {
  const { t } = useTranslation();
  const { handlePaste, handleCopy } = useCopyPaste();
  const modalOpen = useSelector(isModalOpen);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { brainId } = useParams();
  const { deleteNode } = useMenu();
  const { brain } = useBrains(brainId);
  const accountId = useSelector(selectAccountId);
  const features = useSelector(selectFeatureFlags);
  const showAIEnhancement = languageSupportsEnhance(brain?.language, accountId, features);

  const name =
    node.type === 'unknown' && !isRootNodeUnknown ? t('actions.types.unknown') : node.name;
  const isReminder = Boolean(node?.is_reminder);
  const hideActionsMenu = isRootNodeUnknown || isReminder;

  const handleDelete = () => deleteNode(nodeId, name as string);

  useHotkeys(
    'mod+v',
    () => handlePaste(),
    { enabled: canWrite && isSelected && !modalOpen, enableOnFormTags: false },
    [handlePaste, canWrite, isSelected, modalOpen]
  );

  useHotkeys(
    'mod+c',
    () => handleCopy(node),
    { enabled: isSelected && !modalOpen, enableOnFormTags: false },
    [handleCopy, node, isSelected, modalOpen]
  );

  useHotkeys(
    'delete',
    handleDelete,
    { enabled: canWrite && isSelected && !modalOpen, enableOnFormTags: false },
    [handleDelete, canWrite, isSelected, modalOpen]
  );

  return (
    <div className={styles.header}>
      {iconByType({
        // Override type if it is a node created by reminder action
        id: isReminder ? 'reminder' : type,
        size: 24,
        color: 'var(--muted-foreground)',
      })}
      <p>
        <span className="text-sm leading-5 font-semibold text-primary">{name}</span>
      </p>

      <div className={styles.header__icons}>
        {showAIEnhancement && !isReminder && (
          <InfoTooltip
            content={
              node?.enhance
                ? t('ai_agents.enhancement_enabled')
                : t('ai_agents.enhancement_disabled')
            }
            side="bottom"
            showArrow
          >
            <SparklesIcon size={16} color={node?.enhance ? 'var(--ai)' : undefined} />
          </InfoTooltip>
        )}
        {!hideActionsMenu && (
          <ActionMenu
            trigger={
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                className={menuTriggerClassName}
                aria-label="menu-btn"
                aria-controls="card-menu"
                aria-haspopup
              >
                <EllipsisVerticalIcon size={20} />
              </Button>
            }
            handleDelete={handleDelete}
            handleCopy={handleCopy}
            handlePaste={() => handlePaste(node)}
            objectToCopy={node}
          />
        )}
      </div>
    </div>
  );
};

export default NodeHeader;
